export const VIEW_ALL_DEALS_PERMISSION = 'view_all_deals';

/**
 * Working every deal, without being an admin.
 *
 * view_all_deals opens the tracker; this one lets somebody actually
 * work what they can see. The alternative was making a senior landman
 * an admin, which also hands over billing, tenant settings, user
 * accounts and everyone's lead claims.
 *
 * In the database this is one function — can_edit_deal — and it gates
 * insert, update AND delete on every table hanging off a deal: offers,
 * documents, todos, activities, closings, signature requests, ownership
 * records, certified mail. So this grant carries delete with it, the
 * same way it does for an admin.
 */
export const EDIT_ALL_DEALS_PERMISSION = 'edit_all_deals';

export function hasPermission(user, permission) {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

/**
 * Can this person see every deal, or only the ones they are on?
 *
 * An admin always can. So can anyone granted `view_all_deals` — which
 * is how a senior landman gets the whole tracker without also being
 * given the keys to billing, tenant settings and everyone's claims.
 *
 * The database has always known this: the deals read policy asks
 * can_view_all_deals(), which checks the role OR the permission. The
 * browser asked only about the role, so the rows arrived and the screen
 * filtered them straight back out — the permission could be granted and
 * nothing whatsoever would change. Both halves ask the same question
 * now.
 */
export function canViewAllDeals(user) {
  return user?.role === 'admin'
    || hasPermission(user, VIEW_ALL_DEALS_PERMISSION)
    // Editing implies seeing. Without this, granting only edit_all_deals
    // leaves somebody able to write a deal the tracker will not show
    // them — the database makes the same implication.
    || hasPermission(user, EDIT_ALL_DEALS_PERMISSION);
}

export function canEditDeal(user, deal) {
  if (!user?.id || !deal) return false;
  if (user.role === 'admin') return true;
  if (hasPermission(user, EDIT_ALL_DEALS_PERMISSION)) return true;
  return (
    deal.createdBy === user.id
    || deal.buySideAgentId === user.id
    || deal.sellSideAgentId === user.id
    || deal.coordinatorId === user.id
  );
}

/**
 * A deal nobody has taken.
 *
 * Coordinators cold call and raise the deal; until an acquisition
 * manager puts their name on it the buy side is empty. Strictly `null`,
 * which is what the database holds and what the tracker writes when a
 * coordinator creates one. `undefined` is a row that was read without
 * the column, and a trimmed read must not open the book — the server
 * copy makes the same distinction with `'buy_side_agent_id' in deal`.
 */
export function isUnclaimedDeal(deal) {
  return !!deal && deal.buySideAgentId === null;
}

/**
 * Who may SEE a deal. Seeing is not editing: canEditDeal stays as it is.
 *
 * Admins and the two grants see everything. Otherwise the people on
 * the deal — creator, either agent, coordinator — and, until somebody
 * claims it, everybody: an unclaimed deal is visible precisely so that
 * an acquisition manager can take it. The moment one does, this same
 * rule narrows it to them, the coordinator and the admins, and it
 * leaves the other managers' boards.
 *
 * This mirrors public.can_view_deal (and the deals_authenticated_read
 * policy that inlines it) and api/_lib/deal-access.js. The database
 * decides what the browser is sent; this decides what the browser
 * keeps. Before it carried the unclaimed clause the browser was
 * handed every open deal and threw them away, so nobody could claim
 * one.
 */
export function canViewDeal(user, deal) {
  if (!deal) return false;
  if (canViewAllDeals(user)) return true;
  if (!user?.id) return false;
  return (
    deal.createdBy === user.id
    || deal.buySideAgentId === user.id
    || deal.sellSideAgentId === user.id
    || deal.coordinatorId === user.id
    || isUnclaimedDeal(deal)
  );
}
